import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  Input,
  Snackbar,
  Typography,
} from '@mui/joy';
import { Paper, Typography as MuiTypography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import StraightenIcon from '@mui/icons-material/Straighten';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import { getAllSettings, updateSettingById } from '../../../main/API/settings';
import { translateAxiosErrorMessage } from '../../../main/utils/masterApiError';

const SETTINGS = [
  { name: 'DIM_DIVISOR_AIR', titleKey: 'dimDivisors.airTitle', hintKey: 'dimDivisors.airHint', fallbackTitle: 'Air divisor (DIM_DIVISOR_AIR)', fallbackHint: 'IATA cm³ / kg used to auto-compute volumetric weight from W*L*H.', defaultValue: '5000' },
  { name: 'DIM_DIVISOR_GROUND', titleKey: 'dimDivisors.groundTitle', hintKey: 'dimDivisors.groundHint', fallbackTitle: 'Ground divisor (DIM_DIVISOR_GROUND)', fallbackHint: 'Reserved for ground / sea workflows.', defaultValue: '6000' },
];

const POSITIVE_NUMBER_RE = /^[0-9]+(\.[0-9]+)?$/;

/**
 * Editor for the runtime-configurable dimensional divisors used by the
 * per-piece weight & pricing rollup release. The backend stores both values
 * as `Setting` rows; `ShipmentPiece.save()` currently reads `DIM_DIVISOR_AIR`
 * to populate `volumetric_weight_kg` when blank. Ops can tune both per market
 * without code changes (see FRONTEND_PER_PIECE_WEIGHT_PRICING_GUIDE §8).
 */
export default function DimensionDivisorsSetting() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const canList = hasPermission(PERMISSIONS.SETTINGS_LIST);
  const canUpdate = hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL);

  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState(null);
  const [rows, setRows] = useState({});
  const [drafts, setDrafts] = useState({});
  const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!canList) return;
    setLoading(true);
    setError(null);
    try {
      const settings = await getAllSettings();
      const list = Array.isArray(settings) ? settings : [];
      const next = {};
      const nextDrafts = {};
      SETTINGS.forEach(({ name }) => {
        const found = list.find((s) => s && s.name === name) || null;
        next[name] = found;
        nextDrafts[name] = found && typeof found.value === 'string' ? found.value : '';
      });
      setRows(next);
      setDrafts(nextDrafts);
    } catch (e) {
      setError(translateAxiosErrorMessage(e, t) || t('settingsPages.common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [canList, t]);

  useEffect(() => { load(); }, [load]);

  const isValid = useCallback(
    (val) => typeof val === 'string' && POSITIVE_NUMBER_RE.test(val.trim()) && parseFloat(val) > 0,
    []
  );

  const handleSave = useCallback(async (name) => {
    const setting = rows[name];
    const draft = (drafts[name] || '').trim();
    if (!canUpdate || !setting || !isValid(draft)) return;
    setSavingName(name);
    setError(null);
    try {
      await updateSettingById(setting.id, {
        name: setting.name,
        value: draft,
        description: setting.description || '',
      });
      setRows((prev) => ({ ...prev, [name]: { ...setting, value: draft } }));
      setSnack({ open: true, color: 'success', message: t('dimDivisors.savedSuccess', { defaultValue: 'Divisor updated.' }) });
    } catch (e) {
      setSnack({
        open: true,
        color: 'danger',
        message: translateAxiosErrorMessage(e, t) || t('dimDivisors.saveFailed', { defaultValue: 'Could not save divisor.' }),
      });
    } finally {
      setSavingName(null);
    }
  }, [rows, drafts, canUpdate, isValid, t]);

  const previews = useMemo(() => {
    const previewForDivisor = (val) => {
      const d = parseFloat(val);
      if (!Number.isFinite(d) || d <= 0) return null;
      // 40 × 30 × 20 cm sample box → 24000 cm³
      return (40 * 30 * 20 / d).toFixed(3);
    };
    return {
      DIM_DIVISOR_AIR: previewForDivisor(drafts.DIM_DIVISOR_AIR),
      DIM_DIVISOR_GROUND: previewForDivisor(drafts.DIM_DIVISOR_GROUND),
    };
  }, [drafts]);

  return (
    <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#EEF2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                }}
              >
                <StraightenIcon sx={{ color: '#4F46E5', fontSize: 24 }} />
              </Box>
              <Box>
                <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                  {t('dimDivisors.title', { defaultValue: 'Pricing & dimensions' })}
                </MuiTypography>
                <MuiTypography variant="body2" color="#6B7280">
                  {t('dimDivisors.subtitle', { defaultValue: 'Dimensional weight divisors used to auto-compute volumetric weight from W × L × H.' })}
                </MuiTypography>
              </Box>
            </Box>
            <Divider />
          </Box>

          <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
            {t('dimDivisors.notice', {
              defaultValue: 'Changing these values affects new shipment piece save() operations. Historical pieces keep their stored volumetric weight.',
            })}
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {SETTINGS.map(({ name, titleKey, hintKey, fallbackTitle, fallbackHint, defaultValue }) => {
                const setting = rows[name];
                const draft = drafts[name] || '';
                const valid = isValid(draft);
                const dirty = setting && draft !== (setting.value || '');
                const preview = previews[name];
                return (
                  <Grid item xs={12} md={6} key={name}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #F3F4F6',
                        background: '#FFFFFF',
                        height: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ mb: 1.5 }}>
                        <Typography level="title-sm" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.5 }}>
                          {t(titleKey, { defaultValue: fallbackTitle })}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: '#6B7280' }}>
                          {t(hintKey, { defaultValue: fallbackHint })}
                        </Typography>
                      </Box>
                      {!setting && (
                        <Alert color="warning" variant="soft" sx={{ mb: 1 }}>
                          {t('dimDivisors.missingSetting', {
                            defaultValue: 'Setting "{{name}}" was not found in the backend. Default {{value}} is used until configured.',
                            name,
                            value: defaultValue,
                          })}
                        </Alert>
                      )}
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={draft}
                        placeholder={defaultValue}
                        color={valid ? 'neutral' : 'danger'}
                        onChange={(e) => setDrafts((prev) => ({ ...prev, [name]: e.target.value }))}
                        disabled={!setting || savingName === name || !canUpdate}
                      />
                      {preview != null && (
                        <FormHelperText sx={{ mt: 0.5, color: 'text.tertiary' }}>
                          {t('dimDivisors.previewSampleBox', {
                            defaultValue: 'Preview: a 40 × 30 × 20 cm box → {{kg}} kg volumetric.',
                            kg: preview,
                          })}
                        </FormHelperText>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <PermissionGate permissions={[PERMISSIONS.SETTINGS_UPDATE_DETAIL]}>
                          <Button
                            type="button"
                            variant="solid"
                            color="primary"
                            size="sm"
                            startDecorator={savingName === name ? <CircularProgress sx={{ width: 16, height: 16 }} /> : <SaveIcon />}
                            onClick={() => handleSave(name)}
                            disabled={!setting || !canUpdate || !valid || !dirty || savingName === name}
                            sx={{ minWidth: 100 }}
                          >
                            {savingName === name
                              ? t('settingsPages.common.saving')
                              : t('actions.save')}
                          </Button>
                        </PermissionGate>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
              {error && (
                <Grid item xs={12}>
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Paper>

      <Snackbar
        autoHideDuration={3000}
        open={snack.open}
        variant="soft"
        color={snack.color}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false })); }}
      >
        {snack.message}
      </Snackbar>
    </PermissionGate>
  );
}
